import { NextResponse } from "next/server";
import { NextRequest } from "next/server";
import { getToken } from "next-auth/jwt";

/**
 * Where sign-in has to land the browser back.
 *
 * Two things have to line up and neither can be trusted on its own:
 * `req.nextUrl.pathname` comes with the app's `basePath` already stripped
 * (`/dashboard/rep` arrives here as `/rep`), and the `*_URL` variables carry
 * that same segment in some environments and not in others. So the segment is
 * appended here exactly once, from the app's own `basePath`. Without it
 * /dashboard/rep came back from sign-in as /rep, which no app serves.
 */
const callbackUrlFor = (appUrl: string, basePath: string, req: NextRequest) => {
  const origin = appUrl.replace(/\/+$/, "");
  const root =
    basePath && !origin.endsWith(basePath) ? `${origin}${basePath}` : origin;

  return `${root}${req.nextUrl.pathname}${req.nextUrl.search}`;
};

/** `basePath` must mirror the app's next.config.ts. */
export const createMiddleware = (appUrl: string, basePath = "") => {
  return async function middleware(req: NextRequest) {
    const token = await getToken({ req, secret: process.env.NEXTAUTH_SECRET });

    if (!token) {
      const signInUrl = new URL(`${process.env.NEXTAUTH_URL}/auth`);
      signInUrl.searchParams.set(
        "callbackUrl",
        callbackUrlFor(appUrl, basePath, req),
      );

      return NextResponse.redirect(signInUrl);
    }

    return NextResponse.next();
  };
};
